import { type FC, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useSpec } from '../../contexts/SpecContext';
import styles from './Sidebar.module.css';

export const Sidebar: FC = () => {
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [criticalHighCount, setCriticalHighCount] = useState<number>(0);
  const { hasPermission } = useAuth();
  const { activeSpec, clearSpec, hasActiveSpec } = useSpec();
  const navigate = useNavigate();

  const blockedTabs = activeSpec?.blockedTabs || [];

  // useEffect(() => {
  //     loadCriticalHighCount();
  //     const interval = setInterval(loadCriticalHighCount, 30000);
  //     return () => clearInterval(interval);
  // }, []);

  const _loadCriticalHighCount = async () => {
    try {
      const API_URL = import.meta.env.VITE_API_URL;
      const response = await fetch(`${API_URL}/api/vulnerabilities/stats/dashboard`);
      if (response.ok) {
        const data = await response.json();
        const severityMap = data.data?.by_severity || [];
        const critical = severityMap.find((s) => s.severity === 'critical')?.count || 0;
        const high = severityMap.find((s) => s.severity === 'high')?.count || 0;
        setCriticalHighCount(critical + high);
      }
    } catch (error) {
      console.error('Failed to load vulnerability count:', error);
    }
  };

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);

  const isTabVisible = (permission?: string, tabId?: string): boolean => {
    if (permission && !hasPermission(permission)) return false;
    if (tabId && blockedTabs.includes(tabId)) return false;
    return true;
  };

  return (
    <>
      <button className={styles.mobileToggle} onClick={toggleSidebar}>
        |||
      </button>

      <div className={styles.overlay} data-open={isOpen ? 'true' : 'false'} onClick={closeSidebar} />

      <aside className={styles.sidebarContainer} data-open={isOpen ? 'true' : 'false'}>
        <div className={styles.sidebarHeader}>
          <div className={styles.logo}>
            <div className={styles.logoIcon}>W</div>
            <div>
              <div className={styles.logoText}>Security Testing</div>
              <div className={styles.logoSubtext}>by Wallarm</div>
            </div>
          </div>
        </div>

        <nav className={styles.navigation}>
          <div className={styles.navSection}>
            <div className={styles.navSectionTitle}>Setup</div>
            {isTabVisible('schemas.read') && (
              <NavLink className={styles.navItem} to="/specifications" onClick={closeSidebar}>
                <span className={styles.navIcon}>S</span>
                Specifications
              </NavLink>
            )}
            <NavLink className={styles.navItem} to="/proxy-config" onClick={closeSidebar}>
              <span className={styles.navIcon}>P</span>
              Proxy Settings
            </NavLink>
          </div>

          <div className={styles.activeSpecSection}>
            {hasActiveSpec && activeSpec ? (
              <div
                className={styles.activeSpecCard}
                onClick={() => navigate('/specifications')}
                title="Click to view all specifications"
              >
                <div className={styles.specTypeIcon} data-category={activeSpec.specCategory}>
                  {activeSpec.specCategory === 'postman' ? 'PM' : 'OA'}
                </div>
                <div className={styles.specInfo}>
                  <div className={styles.specName}>{activeSpec.name}</div>
                  <div className={styles.specMeta}>
                    {activeSpec.specCategory === 'postman' ? 'Postman' : 'OpenAPI'}
                    {activeSpec.version && ` v${activeSpec.version}`}
                    {activeSpec.endpointCount ? ` \u00B7 ${activeSpec.endpointCount} endpoints` : ''}
                  </div>
                </div>
                <button
                  className={styles.clearSpecBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    clearSpec();
                    navigate('/specifications');
                  }}
                  title="Clear active specification"
                >
                  {'\u2715'}
                </button>
              </div>
            ) : (
              <div className={styles.noSpecPrompt} onClick={() => navigate('/specifications')}>
                No spec selected — click to choose
              </div>
            )}
          </div>

          {hasActiveSpec && (
            <div className={styles.navSection}>
              <div className={styles.navSectionTitle}>Testing</div>
              {isTabVisible('strategies.read', 'business-flow') && (
                <NavLink className={styles.navItem} to="/business-flows" onClick={closeSidebar}>
                  <span className={styles.navIcon}>F</span>
                  Business Flows
                </NavLink>
              )}
              {isTabVisible('strategies.read', 'strategies-library') && (
                <NavLink className={styles.navItem} to="/strategies" onClick={closeSidebar}>
                  <span className={styles.navIcon}>C</span>
                  Strategies Library
                </NavLink>
              )}
              {isTabVisible(undefined, 'passive-scans') && (
                <NavLink className={styles.navItem} to="/passive-scans" onClick={closeSidebar}>
                  <span className={styles.navIcon}>P</span>
                  Passive Scans
                </NavLink>
              )}
              {isTabVisible(undefined, 'test-hypothesis') && (
                <NavLink className={styles.navItem} to="/test-hypotheses" onClick={closeSidebar}>
                  <span className={styles.navIcon}>H</span>
                  Test Hypotheses
                </NavLink>
              )}
              {isTabVisible('tests.execute') && (
                <NavLink className={styles.navItem} to="/test-execution" onClick={closeSidebar}>
                  <span className={styles.navIcon}>E</span>
                  Test Execution
                </NavLink>
              )}
              {isTabVisible('vulnerabilities.read') && (
                <NavLink className={styles.navItem} to="/vulnerabilities" onClick={closeSidebar}>
                  <span className={styles.navIcon}>V</span>
                  Vulnerabilities
                  {criticalHighCount > 0 && <span className={styles.navBadge}>{criticalHighCount}</span>}
                </NavLink>
              )}
            </div>
          )}

          {isTabVisible('users.read') && (
            <div className={styles.navSection}>
              <div className={styles.navSectionTitle}>Administration</div>
              <NavLink className={styles.navItem} to="/users" onClick={closeSidebar}>
                <span className={styles.navIcon}>U</span>
                Users
              </NavLink>
            </div>
          )}
        </nav>
      </aside>
    </>
  );
};
